import { load } from "@cashfreepayments/cashfree-js";
import { toast } from "sonner";
import api, { formatApiError } from "./api";

let _cashfreeSDK = null;
async function _getSDK(mode) {
  if (_cashfreeSDK) return _cashfreeSDK;
  _cashfreeSDK = await load({ mode: mode === "production" ? "production" : "sandbox" });
  return _cashfreeSDK;
}

/**
 * Start a Cashfree hosted checkout for a booking or rental.
 * Backend must return { provider: "cashfree", order_id, payment_session_id, mode }.
 *
 * @param {object} args
 * @param {"booking"|"rental"} args.kind
 * @param {string} args.refId
 * @param {function} args.onSuccess called with the verified entity after "PAID"
 * @param {function} args.onFail   called on any failure with an Error
 */
export async function startCashfreeCheckout({ kind, refId, onSuccess, onFail }) {
  try {
    const { data: order } = await api.post(`/payments/create-order/${kind}/${refId}`);
    if (order.provider !== "cashfree") throw new Error("Backend not set to Cashfree");

    const cashfree = await _getSDK(order.mode);
    const result = await cashfree.checkout({
      paymentSessionId: order.payment_session_id,
      redirectTarget: "_self",
      returnUrl: `${window.location.origin}/payment/return?order_id=${order.order_id}`,
    });

    // For _self redirectTarget the browser navigates away; the code below runs only
    // on _modal returns (which we don't use). Verification happens on the return page.
    if (result?.error) {
      onFail?.(new Error(result.error.message || "Payment failed"));
    }
  } catch (e) {
    const msg = formatApiError(e.response?.data?.detail) || e.message || "Payment failed";
    toast.error(msg);
    onFail?.(e);
  }
}

/**
 * Called from /payment/return page to verify the final status server-side.
 * @param {string} orderId - the cf_... order_id from ?order_id=
 * @returns {{ kind, order_status, entity }}
 */
export async function verifyCashfreeOrder(orderId) {
  const { data } = await api.get(`/payments/cashfree/verify/${orderId}`);
  return data;
}
